import '../styles/LandingClosingInvitation.css';
import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { useProjectCollection } from '../lib/projectData';

const ARC_VIEWBOX = '0 0 1440 220';
const ARC_FILL = 'M0 220 L0 150 C 260 40, 620 -30, 980 60 S 1320 170, 1440 96 L1440 220 Z';
const ARC_HAIR = 'M0 150 C 260 40, 620 -30, 980 60 S 1320 170, 1440 96';
const ARC_GOLD = 'M0 164 C 260 54, 620 -16, 980 74 S 1320 184, 1440 110';

export default function LandingClosingInvitation() {
  const { data, loading, error } = useProjectCollection('profiles');
  const bandRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: bandRef,
    offset: ['start end', 'center center'],
  });

  const fillInset = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.5));
    return `inset(0 ${((1 - p) * 100).toFixed(2)}% 0 0)`;
  });
  const hairPath = useTransform(scrollYProgress, [0.08, 0.7], [0, 1]);
  const goldPath = useTransform(scrollYProgress, [0.18, 0.85], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.25, 0.95], [18, 0]);
  const contentOpacity = useTransform(scrollYProgress, [0.25, 0.9], [0, 1]);

  const records = Array.isArray(data) ? data : [];
  const profile = records.find((r) => r && r.id === 'umang-suthar') || null;

  const arcStyle = reduced
    ? { fillClip: { clipPath: 'inset(0 0% 0 0)' }, pathHair: 1, pathGold: 1, y: 0, opacity: 1 }
    : { fillClip: { clipPath: fillInset }, pathHair: hairPath, pathGold: goldPath, y: contentY, opacity: contentOpacity };

  let identity;
  if (loading) {
    identity = <p className="lci-status" role="status">Loading profile</p>;
  } else if (error) {
    identity = <p className="lci-status" role="alert">Profile details are unavailable.</p>;
  } else if (!profile) {
    identity = <p className="lci-status">Profile details are not available.</p>;
  } else {
    identity = (
      <div className="lci-identity">
        <span className="lci-rule" aria-hidden="true" />
        <h2 id="lci-wordmark" className="lci-wordmark">{profile.displayName}</h2>
        <p className="lci-title">{profile.title}</p>
      </div>
    );
  }

  return (
    <section ref={bandRef} className="lci-band" aria-labelledby="lci-wordmark">
      <div className="lci-arc-wrap" aria-hidden="true">
        <svg
          className="lci-arc-svg"
          viewBox={ARC_VIEWBOX}
          preserveAspectRatio="none"
          focusable="false"
        >
          <motion.path className="lci-arc-fill" d={ARC_FILL} style={arcStyle.fillClip} />
          <motion.path
            className="lci-arc-hair"
            d={ARC_HAIR}
            vectorEffect="non-scaling-stroke"
            style={{ pathLength: arcStyle.pathHair }}
          />
          <motion.path
            className="lci-arc-gold"
            d={ARC_GOLD}
            vectorEffect="non-scaling-stroke"
            style={{ pathLength: arcStyle.pathGold }}
          />
        </svg>
      </div>

      <div data-content-rail="primary" className="lci-inner">
        <motion.div
          className="lci-reveal"
          style={{ y: arcStyle.y, opacity: arcStyle.opacity }}
        >
          {identity}
          <a className="lci-link" href="/contact">
            Get in touch
          </a>
        </motion.div>
      </div>
    </section>
  );
}